import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../store";
import { selectGroupLeader, setSelectedLeader } from "../../../../reducers/groupLeaderFilter";
import type { GroupLeader, GroupLeaderNode, RecordRef } from "../../../../services/ActionReports/ActionReportAPI.types";
import { leaderOptionKey } from "../groupLeaderGrouping";
import { IconArrowDropdown } from "../../../../common/icons/IconArrowDropdown/IconArrowDropdown";
import { IconArrowUp } from "../../../../common/icons/IconArrowUp/IconArrowUp";
import defaultContent from "../../../../content/actionReports";
import { LeafBackdrop } from "../../../../common/leaf/LeafBackdrop/LeafBackdrop";
import LeafRectangleLoader from "../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { getDisplayName } from "../../../../common/util/displayName";
import "./GroupLeaderFilter.scss";

interface Props {
  leaders: GroupLeader[];
  tree: GroupLeaderNode[];
  /**
   * When true, render the trigger label over a skeleton loader instead of
   * the dropdown. Reports gate this on the Japan-only group-leader config
   * flag so non-JP markets never see a placeholder.
   */
  isLoading?: boolean;
  /**
   * `aff-abo` keys for leaders who have at least one record on the
   * currently-visible tab. When supplied, leaders absent from this set are
   * hidden from the dropdown — switching to a tab where they have records
   * brings them back. When undefined, every leader from `leaders`/`tree`
   * is shown.
   */
  activeLeaderKeys?: Set<string>;
  /**
   * Override the "all leaders" option label. Defaults to the shared
   * `filter.groupLeader.allOption` content ("All New ABOs / PCs"), which
   * fits Who's New. Other reports (Renewals, Birthdays, Who's Close) pass
   * "All ABOs" since their record set isn't restricted to new ABOs/PCs.
   */
  allOptionLabel?: string;
  /**
   * Data-testid for the outer wrapper so report-level tests can locate the
   * filter without coupling to visual classes.
   */
  "data-testid"?: string;
  onSelectTrack?: () => void;
}

interface FlatOption {
  aff: string;
  abo: string;
  name: string;
  pinLevel: number;
  depth: number;
}

const flattenTree = (nodes: GroupLeaderNode[], depth = 0): FlatOption[] => {
  const out: FlatOption[] = [];
  for (const node of nodes) {
    out.push({
      aff: node.aff,
      abo: node.abo,
      name: node.name,
      pinLevel: node.pinLevel,
      depth,
    });
    if (node.children?.length) {
      out.push(...flattenTree(node.children, depth + 1));
    }
  }
  return out;
};

/**
 * Japan Group Leader filter rendered in the Action Reports header when the
 * backend response carries at least one `groupLeaders` entry. Matches the
 * Figma: two blue-bordered rounded containers stacked vertically — the
 * collapsed trigger on top with the current selection, and the open
 * selection list below. The currently-selected row is highlighted light
 * gray with bold blue text. No search input, pin badges, or row counts.
 *
 * Selection is stored in the `groupLeaderFilter` slice; this component
 * doesn't read market config or country code.
 */
export const GroupLeaderFilter = ({
  leaders,
  tree,
  activeLeaderKeys,
  allOptionLabel: allOptionLabelOverride,
  isLoading = false,
  "data-testid": dataTestId,
  onSelectTrack,
}: Props) => {
  const { t } = useTranslation(["actionReports", "common"]);
  const dispatch = useAppDispatch();
  const selectedLeader = useAppSelector(selectGroupLeader);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  // Tree drives depth indentation in the open state. Fall back to the flat
  // list if the tree is unavailable — the error boundary handles malformed
  // trees separately. When `activeLeaderKeys` is supplied, hide leaders
  // who have no records on the currently-visible tab.
  const options = useMemo<FlatOption[]>(() => {
    const all =
      tree && tree.length > 0
        ? flattenTree(tree)
        : leaders.map((l) => ({
            aff: l.aff,
            abo: l.abo,
            name: l.name,
            pinLevel: l.pinLevel,
            depth: 0,
          }));
    if (!activeLeaderKeys) return all;
    return all.filter((o) => activeLeaderKeys.has(leaderOptionKey({ aff: o.aff, abo: o.abo })));
  }, [tree, leaders, activeLeaderKeys]);

  const allOptionLabel =
    allOptionLabelOverride ?? t("filter.groupLeader.allOption", defaultContent.filter.groupLeader.allOption);
  const filterLabel = t("filter.groupLeader.label", defaultContent.filter.groupLeader.label);

  const selectedLabel = useMemo(() => {
    if (!selectedLeader) return allOptionLabel;
    const hit = options.find((o) => o.aff === selectedLeader.aff && o.abo === selectedLeader.abo);
    return hit?.name ? getDisplayName(hit.name, t) : `${selectedLeader.aff}-${selectedLeader.abo}`;
  }, [selectedLeader, options, allOptionLabel, t]);

  const applySelection = (ref: RecordRef | null) => {
    dispatch(setSelectedLeader(ref));
    setIsOpen(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.dispatchEvent(new Event("scroll"));
      });
    });
  };

  const isAllSelected = selectedLeader === null;

  if (isLoading) {
    return (
      <div className="GroupLeaderFilter GroupLeaderFilter--loading" data-testid={dataTestId}>
        <div className="GroupLeaderFilter__trigger" aria-busy="true">
          <span className="GroupLeaderFilter__text">
            <span className="GroupLeaderFilter__label">{filterLabel}</span>
            <LeafRectangleLoader isLoading height="1.25rem" width="100%" margin="0.25rem 0 0" />
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`GroupLeaderFilter${isOpen ? " GroupLeaderFilter--open" : ""}`} data-testid={dataTestId}>
      <button
        type="button"
        className="GroupLeaderFilter__trigger"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => {
          if (!isOpen) onSelectTrack?.();
          setIsOpen((prev) => !prev);
        }}
      >
        <span className="GroupLeaderFilter__text">
          <span className="GroupLeaderFilter__label">{filterLabel}</span>
          <span className="GroupLeaderFilter__value">{selectedLabel}</span>
        </span>
        <span className="GroupLeaderFilter__chevron" aria-hidden="true" data-testid="group-leader-filter-chevron">
          {isOpen ? <IconArrowUp backgroundClass="black" /> : <IconArrowDropdown backgroundClass="black" />}
        </span>
      </button>

      {isOpen && (
        <>
          <LeafBackdrop variant="invisible" handleBackdropClick={() => setIsOpen(false)} />
          {/*
           * Stop click + wheel from bubbling into the backdrop / page. The
           * backdrop sits at z-index layers.$backdrop (40), and parents may
           * also listen for wheel to close popovers — without this the menu
           * is unselectable and unscrollable when those listeners fire.
           */}
          <div
            className="GroupLeaderFilter__menu"
            role="listbox"
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            onWheel={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div
              role="option"
              tabIndex={0}
              aria-selected={isAllSelected}
              className={`GroupLeaderFilter__option${isAllSelected ? " GroupLeaderFilter__option--selected" : ""}`}
              onClick={() => applySelection(null)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  applySelection(null);
                }
              }}
            >
              {allOptionLabel}
            </div>

            {options.map((option) => {
              const isSelected = selectedLeader?.aff === option.aff && selectedLeader?.abo === option.abo;
              return (
                <div
                  key={`${option.aff}-${option.abo}`}
                  role="option"
                  tabIndex={0}
                  aria-selected={isSelected}
                  className={`GroupLeaderFilter__option${isSelected ? " GroupLeaderFilter__option--selected" : ""}`}
                  onClick={() => applySelection({ aff: option.aff, abo: option.abo })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      applySelection({ aff: option.aff, abo: option.abo });
                    }
                  }}
                >
                  {getDisplayName(option.name, t)}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
